// The Present-a-buyer wizard's OWN copy home (the recordPaymentCopy /
// transitionsWizardCopy convention): the derived step ladder, and the plain
// explanation each step carries.
//
// Deliberately NOT the test-closed `buyerFilters` label map: that map is the
// enum vocabulary every render site goes through, and folding prose into it
// would put wizard copy behind a completeness test that is about statuses.

import type { BuyerSubmissionStatus } from "@/api/buyers";
import type { StepperStep } from "@/design-system/components/ui";
import { buyerLabel } from "../buyerFilters";
import { SUBMISSION_TERMINAL_STATUSES } from "../submissionPhases";

/**
 * Every step this wizard can draw.
 *
 * `paste` and `listing-review` belong to the create-a-listing branch and exist
 * in the ladder ONLY once that branch is entered — a step drawn before it can
 * be reached promises a decision that usually never arrives
 * (`SendPrequalInviteModal`'s rule, stated there and honoured here).
 */
export type PresentStepKey =
  | "buyer"
  | "listing"
  | "paste"
  | "listing-review"
  | "nda"
  | "terms";

const STEP: Record<PresentStepKey, StepperStep> = {
  buyer: {
    key: "buyer",
    label: "Buyer",
    hint: "Who is being presented.",
  },
  listing: {
    key: "listing",
    label: "Listing",
    hint: "Which business they are being put in front of.",
  },
  paste: {
    key: "paste",
    label: "Paste listing",
    hint: "Paste the marketplace listing so its details can be read out.",
  },
  "listing-review": {
    key: "listing-review",
    label: "Review listing",
    hint: "Check what was extracted before the listing is saved.",
  },
  nda: {
    key: "nda",
    label: "NDA authorisation",
    hint: "Whether Phoenix may sign the broker's NDA for this buyer.",
  },
  terms: {
    key: "terms",
    label: "Terms",
    hint: "The price snapshot, the follow-up, and anything worth recording.",
  },
};

export interface LadderInputs {
  /** The wizard was opened from a buyer's own record — that half is settled. */
  buyerLocked: boolean;
  /** A listing came in with the launch context. Changeable, but skipped. */
  listingLocked: boolean;
  /** The specialist chose to add a listing to the catalog from inside here. */
  creatingListing: boolean;
}

/**
 * The ladder, DERIVED from the launch context rather than fixed.
 *
 * <p>🔴 A prefilled half is not asked for. Opening from a buyer's record and
 * then being made to search for that same buyer is the wizard telling the
 * specialist it was not listening; the same is true of a listing arriving from
 * the row they pressed. So the step is not disabled or pre-answered — it is not
 * drawn at all, and the stepper shows the honest number of decisions left.</p>
 *
 * <p>Because the ladder can grow and shrink mid-flow (entering and leaving the
 * create-a-listing branch), the caller must clamp its index —
 * `steps[Math.min(step, steps.length - 1)]`, the TransitionStartWizard idiom.</p>
 */
export function presentSteps({
  buyerLocked,
  listingLocked,
  creatingListing,
}: LadderInputs): StepperStep[] {
  const steps: StepperStep[] = [];
  if (!buyerLocked) steps.push(STEP.buyer);
  if (!listingLocked) {
    steps.push(STEP.listing);
    if (creatingListing) steps.push(STEP.paste, STEP["listing-review"]);
  }
  steps.push(STEP.nda, STEP.terms);
  return steps;
}

// ---------------------------------------------------------------------------
// Per-step explanation — one sentence each, in plain words.
// ---------------------------------------------------------------------------

/** What the thing being created actually IS. Shown on the first step, whichever
 *  one that turns out to be. */
export const WHAT_A_SUBMISSION_IS =
  "A submission is one buyer put in front of one listing. It is the record the " +
  "whole negotiation hangs off — the conversations, the deal structure, the package sent " +
  "to the listing broker.";

export const BUYER_STEP_BLURB =
  "Pick the buyer being presented. Buyers owned by another consultant are shown " +
  "but cannot be chosen — the server refuses a submission on a buyer you do not " +
  "own, and offering one here would be a guaranteed refusal.";

export const LISTING_STEP_BLURB =
  "Pick the business they are being put in front of. If it is not in the catalog " +
  "yet, add it here — leaving to create it and coming back loses everything typed " +
  "so far.";

export const PASTE_STEP_BLURB =
  "Paste the listing — the page text, or the broker's email. The details and the " +
  "listing agent are pulled out for you to check; nothing is saved until you say so.";

export const LISTING_REVIEW_BLURB =
  "Everything below is editable. The extraction proposes, you are the one who " +
  "saves — and the listing agent is kept as its own record, matched by email, " +
  "rather than being flattened onto the listing.";

export const TERMS_STEP_BLURB =
  "Nearly done. Nothing here is required — a submission can be created on the " +
  "pairing alone and filled in as the conversation develops.";

/** Why the price is copied onto the submission instead of being read live. */
export const PRICE_SNAPSHOT_BLURB =
  "The listing's own asking price legitimately changes afterwards, and the " +
  "negotiation record needs the number that was true when the buyer was " +
  "presented. Leave it blank to snapshot the listing's price as it stands now.";

/** What the specialist should expect the moment they press the button. */
export const WHAT_HAPPENS_NEXT =
  "Presenting opens the submission's own page, where the outreach to the listing " +
  "broker, the NDA, the package and the deal structure are all worked. The buyer moves to " +
  "Actively submitting.";

// ---------------------------------------------------------------------------
// NDA authorisation — explanatory, never a gate.
// ---------------------------------------------------------------------------

/**
 * 🔴 <b>Mirrors `BuyerPackageBlockedException.NoPrequalificationForNda`</b>
 * (`src/PhoenixCapital.Core/Common/BuyerPackageExceptions.cs`) verbatim — the
 * refusal a specialist would otherwise meet three screens later, said here
 * before they present.
 *
 * <p><b>PINNED, not merely requested.</b> This used to carry a comment asking
 * whoever edited the C# to edit this too, and nothing enforced it — the shape of
 * every drift this codebase has been bitten by. `combinedIntro/combinedIntroCopy.parity.test.ts` now
 * reads the C# constant off disk and fails on either side of a divergence, so
 * the server text is the source of truth and this is a copy that cannot go
 * stale. Single-sourcing across the language boundary would need a build step;
 * a parity test is what the display-name ladder and the prequalification funnel
 * both settled on for the same reason.</p>
 */
export const NDA_NOT_AUTHORISED =
  "Phoenix signs the listing broker's NDA on the buyer's behalf, and this buyer " +
  "has not authorised that — the authorisation is given when they complete a " +
  "prequalification. Send this buyer through the prequalification funnel, or link " +
  "an existing one, before recording a signed NDA. If the broker does not require " +
  "an NDA, record that instead.";

/**
 * 🔴 <b>The reason this step exists, and the reason it does not block.</b>
 *
 * <p>The server gate is on recording a <b>signed</b> NDA
 * (<c>BuyerSubmissionService.RecordNdaAsync</c>), not on creating a submission —
 * create has no such precondition. Refusing here would be a hard client-side
 * refusal invented against a server that never refuses. (The already-presented
 * refusal below IS a gate, because the server refuses there — ruling R1.)</p>
 *
 * <p>What it is for: the specialist finds out where the buyer stands BEFORE they
 * present, rather than as a toast three screens later with a listing broker
 * waiting on the phone.</p>
 */
export const NDA_STEP_BLURB =
  "Phoenix signs the listing broker's NDA on the buyer's behalf. A buyer " +
  "authorises that by completing a prequalification, so it is worth knowing " +
  "where this one stands before you present them.";

export const NDA_DOES_NOT_BLOCK =
  "None of this stops you presenting the buyer. The NDA is recorded on the " +
  "submission afterwards, and only a signed one needs the authorisation.";

export const NDA_AUTHORISED =
  "This buyer has completed a prequalification, so the authorisation for Phoenix " +
  "to sign on their behalf is on file. A signed NDA can be recorded on the " +
  "submission as soon as the broker sends one.";

/**
 * The two exits this step does NOT resolve, as guidance rather than as a wall.
 *
 * <p>🔴 The third exit — sending the invitation — used to be a link out to the
 * buyer's Prequalification tab and is now done IN PLACE, which is why it is no
 * longer in this list. See {@link NDA_SEND_INTRO}.</p>
 *
 * <p>Neither of these two carries a control, deliberately. Both live on another
 * screen, and a control that navigates there discards a half-filled wizard —
 * including, on the create-a-listing branch, an extraction that has not been
 * saved. Naming where they are is the whole affordance; taking the specialist
 * there is the bug.</p>
 */
export const NDA_EXITS: readonly { label: string; detail: string }[] = [
  {
    label: "Link a prequalification they have already completed",
    detail:
      "If they went through the funnel before, attach it from the buyer's Prequalification tab. Not offered here — going there now would discard everything typed into this wizard.",
  },
  {
    label: "Record that the broker does not require an NDA",
    detail:
      "Done on the submission once it exists — that branch needs no authorisation, because Phoenix is not signing anything.",
  },
];

// ---------------------------------------------------------------------------
// Sending the invitation, in place.
// ---------------------------------------------------------------------------

/**
 * 🔴 <b>Why the invitation is sent from inside the wizard.</b>
 *
 * <p>The old control was a link to the buyer's Prequalification tab, and it
 * failed in two different ways depending on where the wizard had been opened
 * from. From the buyer's own record it switched the tab UNDERNEATH an open
 * modal, so the specialist closed the wizard onto a page that had silently
 * moved. From anywhere else the route unmounted and took the whole wizard with
 * it — including, on the create-a-listing branch, a pasted-and-extracted listing
 * that had not been saved and could not be recovered.</p>
 *
 * <p>The file already argued the case for the buyer chip beside it: a control
 * that discards a half-filled form is a bug wearing a different hat. This is the
 * same rule applied to the one control that was still doing it.</p>
 */
export const NDA_SEND_INTRO =
  "You can send the invitation from here — nothing in this wizard is lost, and " +
  "you do not have to leave to do it.";

/**
 * What the send does, said BEFORE the button rather than behind a confirm step.
 *
 * <p>A buyer's address is already on their own record, so there is nothing to
 * type and nothing to check — the invitation is one click, exactly as it is on
 * the buyer's own Prequalification tab. (The SELLER invitation is the one that
 * goes through a preview, because this module holds no seller contact record
 * and the address has to be asked for.) Adding a confirm here would put a
 * second click in front of the one action this step exists to unblock.</p>
 *
 * <p>The duplicate refusal is a different matter and IS a confirm — see
 * `DuplicateInviteConfirm`, shared with both invitation surfaces so the
 * sentence a sender reads is written once.</p>
 */
export const NDA_SEND_CONFIRM =
  "The buyer gets an email with a link to the prequalification funnel. The " +
  "authorisation is recorded when they finish it — not when it is sent — so you " +
  "can present them now either way.";

export const NDA_SEND_SENT =
  "Invitation sent. The authorisation lands on this buyer's record when they " +
  "complete the funnel; nothing here waits on it.";

// ---------------------------------------------------------------------------
// The listing picker's three tiers.
// ---------------------------------------------------------------------------

/**
 * 🔴 <b>Why the picker is three tiers and not a search box.</b>
 *
 * <p>It was one search box over a shared catalog, with the buyer standing right
 * there and nothing joining the two: a specialist who knew the budget to the
 * dollar still had to type a business name they were trying to be reminded of.
 * The two things they almost always want — what this buyer is already looking
 * at, and what in the catalog fits them — were both a search away and neither
 * was offered.</p>
 */
export const LISTING_TIER_SHORTLIST_BLURB =
  "Listings already on this buyer's list. Tying one there did not present it — " +
  "this is where that happens.";

export const LISTING_TIER_SUGGESTED_BLURB =
  "Ranked against what this buyer is looking for. Every row says why, including " +
  "where it does not fit — a suggestion you cannot argue with is not one.";

export const LISTING_TIER_SEARCH_BLURB =
  "Everything in the catalog. Searches business name, headline, industry, city, " +
  "marketplace, listing reference and URL.";

/** Shown above the "Not considered" block. */
export const LISTING_NOT_CONSIDERED_BLURB =
  "Not part of the ranking — nothing on a listing answers these, so no score " +
  "could honestly account for them:";

/** The unpriced group's heading rule, said once. */
export const LISTING_NO_PRICE_BLURB =
  "Scored on everything else. A listing with no price is usually one somebody " +
  "just added, which is exactly when hiding it would be worst.";

export const LISTING_NO_CRITERIA_BLURB =
  "This buyer records nothing to match on — no budget, no target industries, no " +
  "areas. Add those on their record and this list becomes useful; until then it " +
  "would only be the newest listings wearing a score.";

// ---------------------------------------------------------------------------
// Already presented — ruling R1: one standing submission per buyer + listing.
// ---------------------------------------------------------------------------

/** What a refusal needs to say why: the standing submission's status, and
 *  whether it is archived. */
export interface StandingPresentation {
  status?: BuyerSubmissionStatus | null;
  isArchived?: boolean | null;
}

/**
 * 🔴 <b>The one sentence for "this pair is already presented"</b> — the wizard's
 * refusal and every picker row it disables read it from here, so the reason a
 * row is greyed and the reason Present is withheld can never disagree.
 *
 * <p>Ruling R1 (owner, final): any NON-DELETED submission for the pair, whatever
 * its status, archived included, refuses a new presentation. There is no
 * "present anyway" — a dead pairing is retried by reopening its submission.</p>
 *
 * @param archivedOn the archive date, already formatted by the caller's instant
 *   formatter; omitted where a row has no room for it.
 */
export function alreadyPresentedReason(
  p: StandingPresentation,
  archivedOn?: string | null,
): string {
  if (p.isArchived) {
    return archivedOn ? `Already presented — archived ${archivedOn}` : "Already presented — archived";
  }
  return p.status ? `Already presented — ${buyerLabel(p.status)}` : "Already presented";
}

/** Why the wizard offers no Present on a presented pair, said once. */
export const ALREADY_PRESENTED_BLURB =
  "A buyer is presented to a listing once — the existing submission is where this " +
  "pairing is worked.";

const TERMINAL = new Set<BuyerSubmissionStatus>(SUBMISSION_TERMINAL_STATUSES);

/** A closed-out submission is retried by reopening it, never by a second one. */
export function alreadyPresentedHint(p: StandingPresentation): string | null {
  return !p.isArchived && p.status && TERMINAL.has(p.status)
    ? "To try again, reopen it with Change status."
    : null;
}
